import React, { useEffect, useMemo, useState } from 'react';
import { useCollectionAPI } from '../hooks/useCollectionAPI';
import { EmployeeData, ICreateEmployee } from '../models/CollectionPoint';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  MaterialReactTable,
  MaterialReactTableProps,
  MRT_ColumnDef,
  MRT_EditActionButtons,
} from 'material-react-table';
import { useAPI } from '../../shared/services/api/API';

export const AddEmployee = () => {
  const {
    allCollectionCenter,
    createBusinessEmployee,
    getAllBusinessEmp,
    getAllBusinessCollectionCenter,
    updateBusEmployee,
    deleteBusEmployee,
    allEmpData,
  } = useCollectionAPI();
  const [formData, setFormData] = useState<ICreateEmployee>({
    name: '',
    employeeId: '',
    campId: '',
    designation: '',
    phone: '',
  });
  const { handleAlertBar } = useAPI();
  const [employeesData, setEmployeesData] = useState<EmployeeData[]>([]);
  const [validationErrors, setValidationErrors] = useState<{
    [cellId: string]: string;
  }>({});
  const columns = useMemo<MRT_ColumnDef<EmployeeData>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 20,
      },
      {
        accessorKey: 'employeeId',
        header: 'Employee Id',
        size: 20,
      },
      {
        accessorKey: 'location',
        header: 'Location',
        size: 20,
      },
      {
        accessorKey: 'designation',
        header: 'Designation',
        size: 20,
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        size: 50,
      },
    ],
    [],
  );
  const [valuesDetail, setValuesDetail] = useState<any>(() =>
    columns.reduce((acc, column) => {
      acc[column.accessorKey ?? ''] = '';
      return acc;
    }, {} as any),
  );
  useEffect(() => {
    const fetchStoresAndEmployees = async () => {
      await getAllBusinessCollectionCenter();
      await getAllBusinessEmp();
    };
    fetchStoresAndEmployees();
  }, []);
  useEffect(() => {
    if (allCollectionCenter.length > 0 && !formData.campId) {
      setFormData((prevData) => ({
        ...prevData,
        campId: allCollectionCenter[0],
      }));
    }
  }, [allCollectionCenter]);
  const handleCreateEmployee = () => {
    const employeeData = {
      name: formData.name,
      employeeId: formData.employeeId,
      campId: formData.campId,
      designation: formData.designation,
      phone: formData.phone,
    };
    createBusinessEmployee(employeeData);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  useEffect(() => {
    setEmployeesData(allEmpData);
    setFormData({
      name: '',
      employeeId: '',
      campId: '',
      designation: '',
      phone: '',
    });
  }, [allEmpData]);
  const handleSaveRowEdits: MaterialReactTableProps<EmployeeData>['onEditingRowSave'] =
    async ({ exitEditingMode, row, values }) => {
      if (!Object.keys(validationErrors).length) {
        const payload = {
          name: valuesDetail.name || values.name,
          employeeId: valuesDetail.employeeId || values.employeeId,
          campId: valuesDetail.campId || values.campId,
          designation: valuesDetail.designation || values.designation,
          phone: valuesDetail.phone || values.phone,
        };
        const updateEmpResponse: any = await updateBusEmployee(
          row?.original.id,
          payload,
        );
        if (updateEmpResponse.status === 200) {
          getAllBusinessEmp();
          handleAlertBar('success', 'Employee details updated successfully');
          exitEditingMode(); //required to exit editing mode and close modal
        } else {
          if (updateEmpResponse.data.error) {
            console.error(
              'Error Updating Business Department: ',
              updateEmpResponse.data.message,
            );
          }
        }
      }
    };

  const handleCancelRowEdits = () => {
    setValidationErrors({});
  };
  return (
    <div style={{ width: '100%' }}>
      <Grid container justifyContent={'space-between'}>
        <Grid item className="mb-1 mt-1 ms-3">
          <Typography
            color={'primary'}
            sx={{ textAlign: 'center' }}
            variant="h5"
            className="mt-0"
          >
            Add Employee
          </Typography>
        </Grid>
      </Grid>
      <div style={{ margin: '5px 15px', borderTop: '2px solid #25BCCA' }}></div>
      <Box sx={{ padding: '20px' }}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Stack spacing={1}>
              <TextField
                size="small"
                key="name"
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                SelectProps={{ MenuProps: { disablePortal: true } }}
              ></TextField>
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Stack spacing={1}>
              <TextField
                size="small"
                key="employeeId"
                label="Employee Id"
                name="employeeId"
                value={formData.employeeId}
                onChange={handleInputChange}
                SelectProps={{ MenuProps: { disablePortal: true } }}
              ></TextField>
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Stack spacing={1}>
              <TextField
                size="small"
                key="designation"
                label="Designation"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                SelectProps={{ MenuProps: { disablePortal: true } }}
              ></TextField>
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Stack spacing={1}>
              <TextField
                size="small"
                key="campId"
                name="campId"
                label="Camp Id"
                select
                value={formData.campId}
                onChange={handleInputChange}
              >
                {allCollectionCenter.map((cId, i) => (
                  <MenuItem key={i} value={cId}>
                    {cId}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Stack spacing={1}>
              <TextField
                size="small"
                key="phone"
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                SelectProps={{ MenuProps: { disablePortal: true } }}
              ></TextField>
            </Stack>
          </Grid>
          <Grid item xs={3}>
            <Stack spacing={1}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateEmployee}
              >
                Create New Employee
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>
      <div style={{ padding: '5px 15px' }}>
        <Paper className="p-2 mb-2">
          <MaterialReactTable
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                width: '100%',
                '& .css-1tbggly': {
                  minHeight: '0px',
                },
                '& .css-10gei56': {
                  minHeight: '0px',
                },
              },
            }}
            muiTableContainerProps={{
              sx: {
                height: 'auto',
              },
            }}
            defaultColumn={{
              maxSize: 150,
              minSize: 10,
              size: 50,
            }}
            displayColumnDefOptions={{
              'mrt-row-actions': {
                muiTableHeadCellProps: {
                  align: 'center',
                },
                size: 20,
              },
            }}
            columns={columns}
            enableFullScreenToggle={false}
            enableStickyHeader
            positionActionsColumn="last"
            enableRowActions={true}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableFilters={false}
            enablePagination={false}
            enableSorting={false}
            enableHiding={false}
            enableDensityToggle={false}
            enableToolbarInternalActions={false}
            data={employeesData}
            editDisplayMode="modal"
            onEditingRowSave={handleSaveRowEdits}
            onEditingRowCancel={handleCancelRowEdits}
            renderEditRowDialogContent={({
              table,
              row,
              internalEditComponents,
            }) => (
              <Dialog fullWidth open={true}>
                <DialogTitle
                  sx={{ backgroundColor: '#83CBEB', color: '#ffff' }}
                  variant="h5"
                  textAlign={'center'}
                  className="p-3 mb-3"
                >
                  Update Employee
                </DialogTitle>
                <DialogContent>
                  <Grid container spacing={3}>
                    {columns.map((column) => (
                      <>
                        {column.accessorKey === 'name' && (
                          <Grid
                            key={column.accessorKey}
                            item
                            xs={12}
                            md={12}
                            lg={12}
                          >
                            <TextField
                              fullWidth
                              key={column.accessorKey}
                              variant="standard"
                              label={column.header}
                              name={column.accessorKey}
                              defaultValue={row.original.name}
                              onChange={(e) =>
                                setValuesDetail({
                                  ...valuesDetail,
                                  [e.target.name]: e.target.value,
                                })
                              }
                            />
                          </Grid>
                        )}
                        {column.accessorKey === 'employeeId' && (
                          <Grid
                            key={column.accessorKey}
                            item
                            xs={12}
                            md={12}
                            lg={12}
                          >
                            <TextField
                              fullWidth
                              key={column.accessorKey}
                              variant="standard"
                              label={column.header}
                              name={column.accessorKey}
                              defaultValue={row.original.employeeId}
                              onChange={(e) =>
                                setValuesDetail({
                                  ...valuesDetail,
                                  [e.target.name]: e.target.value,
                                })
                              }
                            />
                          </Grid>
                        )}
                        {column.accessorKey === 'designation' && (
                          <Grid
                            key={column.accessorKey}
                            item
                            xs={12}
                            md={12}
                            lg={12}
                          >
                            <TextField
                              fullWidth
                              key={column.accessorKey}
                              variant="standard"
                              label={column.header}
                              name={column.accessorKey}
                              defaultValue={row.original.designation}
                              onChange={(e) =>
                                setValuesDetail({
                                  ...valuesDetail,
                                  [e.target.name]: e.target.value,
                                })
                              }
                            />
                          </Grid>
                        )}
                        {column.accessorKey === 'campId' && (
                          <Grid
                            key={column.accessorKey}
                            item
                            xs={12}
                            md={6}
                            lg={6}
                          >
                            <div>
                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  color: 'rgba(0, 0, 0, 0.6)',
                                }}
                              >
                                Item Image
                              </Typography>
                              <Select
                                fullWidth
                                label={column.header}
                                variant="standard"
                                name={column.accessorKey}
                                defaultValue={row.original.campId}
                                onChange={(e) =>
                                  setValuesDetail({
                                    ...valuesDetail,
                                    [e.target.name]: e.target.value,
                                  })
                                }
                              >
                                {allCollectionCenter.map((item: any) => (
                                  <MenuItem key={item} value={item}>
                                    <img src={item} width={30} height={22} />
                                  </MenuItem>
                                ))}
                              </Select>{' '}
                            </div>
                          </Grid>
                        )}
                        {column.accessorKey === 'phone' && (
                          <Grid
                            key={column.accessorKey}
                            item
                            xs={12}
                            md={12}
                            lg={12}
                          >
                            <TextField
                              fullWidth
                              key={column.accessorKey}
                              variant="standard"
                              label={column.header}
                              name={column.accessorKey}
                              defaultValue={row.original.phone}
                              onChange={(e) =>
                                setValuesDetail({
                                  ...valuesDetail,
                                  [e.target.name]: e.target.value,
                                })
                              }
                            />
                          </Grid>
                        )}
                      </>
                    ))}
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <MRT_EditActionButtons
                    variant="text"
                    table={table}
                    row={row}
                  />
                </DialogActions>
              </Dialog>
            )}
            renderRowActions={({ row, table }) => (
              <Box
                sx={{
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Button
                  sx={{ color: 'white' }}
                  variant="contained"
                  onClick={() => table.setEditingRow(row)}
                >
                  {'Update'}
                </Button>
                <Button
                  sx={{ color: 'white' }}
                  variant="contained"
                  onClick={() => deleteBusEmployee(row?.original?.id)}
                >
                  {'Delete'}
                </Button>
              </Box>
            )}
          />
        </Paper>
      </div>
    </div>
  );
};
